import {
    type CSSProperties,
    useState
} from "react";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    CheckBox,
    Switch,
    Label
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Label";

const USAGE_CODE = `<CheckBox
    onPress={handleToggleTerms}
    isChecked={isTermsAccepted}
    id="terms"
/>
<Label
    htmlFor="terms"
    onPress={handleToggleTerms}
>
    Accept the terms
</Label>`;

const SWITCH_CODE = `<View
    style={styles.row}
>
    <Label
        htmlFor="airplane"
        onPress={handleToggleAirplane}
    >
        Airplane mode
    </Label>
    <Switch
        onPress={handleToggleAirplane}
        isChecked={isAirplane}
        id="airplane"
    />
</View>`;

const MARK_CODE = `<Label
    isRequired={true}
>
    Full name
</Label>

<Label
    isOptional={true}
>
    Nickname
</Label>

<Label
    isDisabled={true}
>
    Account number
</Label>`;

const ROW_STYLE: CSSProperties = {
    justifyContent: "space-between",
    alignItems: "center",
    display: "flex",
    width: 280,
    gap: 16
};

const START_STYLE: CSSProperties = {
    alignItems: "center",
    display: "flex",
    gap: 8
};

const STACK_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 16
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("labelPage.switchTitle"),
        id: "switch"
    },
    {
        title: t("labelPage.markTitle"),
        id: "marks"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const LabelPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isTermsAccepted,
        setIsTermsAccepted
    ] = useState(false);

    const [
        isAirplane,
        setIsAirplane
    ] = useState(false);

    const handleToggleTerms = () => setIsTermsAccepted((current) => !current);

    const handleToggleAirplane = () => setIsAirplane((current) => !current);

    return <Article>
        <ComponentHeader
            lead={t("labelPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("labelPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("labelPage.overviewCaption")}
            >
                <div
                    style={START_STYLE}
                >
                    <CheckBox
                        isChecked={isTermsAccepted}
                        onPress={handleToggleTerms}
                        id="label-terms"
                    />
                    <Label
                        onPress={handleToggleTerms}
                        htmlFor="label-terms"
                    >
                        {t("labelPage.terms")}
                    </Label>
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("labelPage.switchTitle")}
            id="switch"
        >
            <Paragraph>
                {t("labelPage.switchText")}
            </Paragraph>
            <Preview>
                <div
                    style={ROW_STYLE}
                >
                    <Label
                        onPress={handleToggleAirplane}
                        htmlFor="label-airplane"
                    >
                        {t("labelPage.airplane")}
                    </Label>
                    <Switch
                        onPress={handleToggleAirplane}
                        isChecked={isAirplane}
                        id="label-airplane"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={SWITCH_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("labelPage.markTitle")}
            id="marks"
        >
            <Paragraph>
                {t("labelPage.markText")}
            </Paragraph>
            <Preview>
                <div
                    style={STACK_STYLE}
                >
                    <Label
                        isRequired={true}
                    >
                        {t("labelPage.fullName")}
                    </Label>
                    <Label
                        isOptional={true}
                    >
                        {t("labelPage.nickname")}
                    </Label>
                </div>
            </Preview>
            <CodeBlock
                code={MARK_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("labelPage.statesText")}
            </Paragraph>
            <Preview>
                <div
                    style={START_STYLE}
                >
                    <CheckBox
                        id="label-disabled"
                        isDisabled={true}
                        isChecked={true}
                    />
                    <Label
                        htmlFor="label-disabled"
                        isDisabled={true}
                    >
                        {t("labelPage.accountNumber")}
                    </Label>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("labelPage.a11yLink"),
                    t("labelPage.a11yRequired"),
                    t("labelPage.a11yOptional"),
                    t("labelPage.a11yInternal")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("labelPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("labelPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("labelPage.propIsOptional"),
                        defaultValue: "false",
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("labelPage.propOptionalText"),
                        name: "optionalText",
                        type: "string"
                    },
                    {
                        description: t("labelPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("labelPage.propVariant"),
                        type: "\"label\" | \"body\"",
                        defaultValue: "\"label\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("labelPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("labelPage.propHtmlFor"),
                        name: "htmlFor",
                        type: "string"
                    },
                    {
                        description: t("labelPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("labelPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("labelPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("labelPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("labelPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default LabelPage;
